import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Emergent-managed Google Auth callback.
 * Reads session_id from URL fragment, POSTs it to the backend, stashes the
 * returned JWT + user, then routes to /portal or /admin depending on role.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;

    if (!sessionId) {
      toast.error("Missing Google session — please try again.");
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        const { data } = await axios.post(`${base}/api/auth/google-session`, { session_id: sessionId });
        if (data?.token) {
          localStorage.setItem("cq_token", data.token);
          setUser(data);
        }
        try { window.history.replaceState(null, "", window.location.pathname); } catch { /* noop */ }
        const dest = ["super_admin", "admin", "credit_specialist", "team_member"].includes(data?.role)
          ? "/admin" : "/portal";
        toast.success(`Welcome, ${data.first_name || data.name || "friend"}!`);
        nav(dest, { replace: true });
      } catch (err) {
        const msg = err?.response?.data?.detail || "Google sign-in failed.";
        toast.error(msg);
        nav("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="text-center">
        <Loader2 className="mx-auto animate-spin text-amber-500" size={44} />
        <p className="mt-4 font-display text-lg font-black text-slate-900">Finishing your sign-in…</p>
        <p className="text-sm text-slate-500 mt-1">One second while we open your Credit Quack account.</p>
      </div>
    </div>
  );
}
